import React, { useCallback, useEffect, useRef, useState } from 'react'
import GHref from 'tennders-market/common/components/element/GHref'
import axios from 'axios'
import useThrottledFunction from 'tennders-quick-search/useThrottledFunction'
import { useRouter } from 'next/router'
import { useDetectClickOutside } from 'react-detect-click-outside'
import styles from './quickSearch.module.css'

type Props = {
  search: string,
  result ?: Record<string, any>,
  isFocused ?: boolean,
  openResult?: (result: Record<string, any>) => void,
}
const types = { 1: 'Quick Action', 2: 'Company', 3: 'Transaction', 4: 'Sales Invoice', 5: 'Credit Note' }
const Item = React.memo(({ result, search, isFocused, openResult }: Props) => {
  // Replace the matched search variable with <strong> tags
  const boldText = result.text.replace(
    new RegExp(`(${search})`, 'gi'), // Case-insensitive search
    '<strong>$1</strong>'
  )

  return (
    <li
      className={styles.liContainer + ' ' + (isFocused ? styles.focusedItem : '')}
      key={result.text + result.url}
    >
      <GHref onClick={() => openResult(result)} className={styles.itemText}>
        {/* Render the text with <strong> tags */}
        <span dangerouslySetInnerHTML={{ __html: boldText }} />
      </GHref>
      {result.type ? (
        <small className={styles.itemSmall}>
          {types[result.type] || ''}
        </small>
      ) : null}
    </li>
  )
}, (prevProps, nextProps) => prevProps.result.id !== nextProps.result.id)

const QuickSearch = () => {
  const [search, setSearch] = useState('')
  const [results, setResults] = useState([])
  const router = useRouter()
  const [show, setShow] = useState(false)

  const inputRef = useRef(null)
  const [focused, setFocused] = useState(0)
  const fetchResults = useCallback(async (search) => {
    return
    const { data } = await axios.post('http://localhost:5027/api/Search', { SearchTerm: search })
    setResults(data)
  }, [])
  const callback = useThrottledFunction({ callbackFn: fetchResults, throttleMs: 21 })

  useEffect(() => {
    callback.throttledFn('')
  }, [])
  const closeMenu = useCallback(() => {
    setShow(false)
    setResults([])
    setSearch('')
    callback.throttledFn('')
  }, [])
  const ref = useDetectClickOutside({ onTriggered: closeMenu })
  const openResult = (result) => {
    closeMenu()
    // Get the current URL
    let currentUrl = window.location.href

    // Remove query parameters from the URL
    currentUrl = currentUrl.split('?')[0]

    const url = result.url.replace('{currentUrl}', currentUrl)

    router.push(url)
  }

  // Function to handle Arrow Down key press
  function handleArrowDown() {
    setFocused(prev => {
      const newItem = prev + 1
      if (newItem > results.length - 1) {
        return results.length - 1
      }
      return newItem
    })
  }

  function handleArrowUp() {
    setFocused(prev => {
      const newItem = prev - 1
      if (newItem > 0) {
        return newItem
      }
      return 0
    })
  }

  // Function to handle Enter key press
  function handleEnter() {
    const item = typeof results[focused] !== 'undefined' ? results[focused] : null
    openResult(item)
  }

  const handleResult = useCallback((event) => {
    // Check if Ctrl (Control) key and T key are pressed simultaneously
    if (event.ctrlKey && event.key === 't') {
      setShow(true)

      // Prevent the default browser behavior (opening a new tab)
      event.preventDefault()
      setTimeout(() => {
        inputRef.current.focus()
      }, 150)
    }
  }, [])

  useEffect(() => {
  // Add event listeners
    document.addEventListener('keydown', handleResult)

    return () => {
    // Remove event listeners
      document.removeEventListener('keydown', handleResult)
    }
  }, [])

  if (!show) {
    return null
  }

  return null

  return (
    <div className={styles.container}>
      <div ref={ref} className={styles.innerContainer}>
        <div className={styles.searchBar}>
          <input
            type="text"
            placeholder="Search"
            className={styles.searchInput}
            value={search}
            onKeyDown={event => {
              if (event.key === 'ArrowDown') {
                handleArrowDown()
              } else if (event.key === 'ArrowUp') {
                handleArrowUp()
              } else if (event.key === 'Enter') {
                handleEnter()
              }
            }}
            ref={inputRef}
            onChange={(e) => {
              setSearch(e.target.value)
              callback.throttledFn(e.target.value)
            }}
          />
        </div>

        <div className={styles.resultsContainer}>
          {results.length > 0 ? (
            <ul className={styles.ulContainer}>
              {results.map((result, i) => (
                <Item key={result.text + result.url} search={search} result={result} isFocused={i === focused} openResult={openResult} />
              ))}
            </ul>
          ) : (
            <div className={styles.noResults}>
              No results
            </div>
          )}
        </div>

      </div>
    </div>
  )
}

export default QuickSearch
